import type { PopoverProps } from "@mantine/core";

/**
 * Space available to a floating overlay's content, published as a CSS custom property.
 *
 * Consumers read this from SCSS, where the name has to be written out as a literal, so the compiler cannot
 * catch a mismatch. It is currently read by `select.module.scss` and `date-picker-input.module.scss` — grep
 * for the literal before renaming it.
 */
export const OVERLAY_AVAILABLE_HEIGHT = "--overlay-available-height";

/** Breathing room left between an overlay and the viewport edge, so it never sits flush against it. */
export const OVERLAY_PADDING = 8;

/**
 * Floor for the published height, in pixels.
 *
 * This is a judgement call rather than a derived value, so here is the reasoning. The cap is consumed by the
 * dropdown's *content*, and the smallest content still worth showing is a header, two or three option rows and
 * a footer — roughly 40 + (3 x 24) + 48, which lands near 120. Publishing less than that produces a dropdown
 * too small to act in.
 *
 * Below the floor we deliberately let the overlay overhang instead of shrinking further: an overlay hanging
 * off the screen can still be dismissed and retried at a larger window, whereas one clamped to nothing looks
 * like the component is broken — which is precisely the bug this whole file exists to fix. It only engages on
 * viewports shorter than roughly 200px, so in practice it is a guard against a degenerate case, not something
 * users meet.
 */
export const MINIMUM_OVERLAY_HEIGHT = 120;

/**
 * Keeps floating overlays inside the viewport on short screens.
 *
 * Mantine's `flip` already moves a dropdown to whichever side of its target has more room, but when the
 * dropdown is taller than the space on *both* sides it simply overhangs the viewport, and because it is
 * portaled and absolutely positioned the overhang is unreachable.
 *
 * This publishes the room available beside the target as a CSS custom property, so the overlay's own
 * content can cap itself to that height and scroll rather than overhang. Nothing here moves the overlay:
 * positioning is left entirely to Mantine's defaults, so an overlay that fits is placed exactly where it
 * always was.
 *
 * Two approaches that look tidier but do not work:
 *
 * - `shift: { crossAxis: true }` to let a tall overlay slide vertically. Mantine 8.3.10 registers `shift`
 *   *before* `flip` (`use-popover.mjs`), so cross-axis shifting resolves the overflow first and `flip` then
 *   never fires — the overlay is dragged over its own target even with plenty of room to flip into.
 * - Letting `size` set `max-height` on the floating element, which is its default behaviour. Some popovers
 *   nest non-portaled dropdowns, which the resulting overflow container would clip — see
 *   `filter-fields.types.ts` and KRS-1054. Publishing a variable is inert until a component opts in.
 *
 * Known limitation: the viewport-relative measurements below assume the viewport is the clipping boundary,
 * which holds for the portaled overlays this is used by today. For a `withinPortal={false}` overlay inside a
 * scroll container, Floating UI's `availableHeight` is the tighter and more correct bound, and taking the
 * maximum discards it — such an overlay could still overhang its scroll container. Revisit with
 * `detectOverflow` if a consumer ever needs that.
 *
 * `apply` runs on every scroll and resize while the overlay is open, so it deliberately stays cheap: one rect
 * read and one computed-style read. The border / padding measurement is intentionally *not* cached — they
 * can change under it (theme switch, responsive rules), and a stale cap is a worse failure than a style read.
 */
export const OVERLAY_FIT_MIDDLEWARES: NonNullable<PopoverProps["middlewares"]> = {
    size: {
        padding: OVERLAY_PADDING,
        apply: ({ availableHeight, elements }) => {
            // Publish the best space on offer either side of the target, not the space the current
            // placement happens to have. Capping to the current placement would shrink the overlay until
            // that placement fit, so `flip` would never see the overflow and would stop flipping — the
            // overlay would squash in place instead of moving to the roomier side.
            const target = elements.reference.getBoundingClientRect();
            const viewportHeight = document.documentElement.clientHeight;
            const best = Math.max(
                target.top - OVERLAY_PADDING,
                viewportHeight - target.bottom - OVERLAY_PADDING,
                availableHeight
            );

            // Consumers apply the published value to content *inside* the dropdown, but `best` measures the
            // space the dropdown *as a whole* has to fit into. So subtract the dropdown's own vertical border
            // and padding: without it the content fills the whole space, the border and padding are added on
            // top, and the overlay ends up exactly that much taller than the room available.
            //
            // Each value is guarded rather than summed directly. A dropdown that is hidden or detached when
            // `apply` runs — `keepMounted` mounts it before it is ever shown — can report an empty string, and
            // `parseFloat("")` is `NaN`, which would poison the total and every value derived from it.
            const { borderTopWidth, borderBottomWidth, paddingTop, paddingBottom } = getComputedStyle(
                elements.floating
            );
            const verticalBorderAndPadding = [borderTopWidth, borderBottomWidth, paddingTop, paddingBottom].reduce(
                (total, value) => {
                    const parsed = parseFloat(value);
                    return total + (Number.isFinite(parsed) ? parsed : 0);
                },
                0
            );

            const available = Math.floor(best - verticalBorderAndPadding);

            if (!Number.isFinite(available)) {
                // Nothing measurable to publish. Remove the property rather than write a bad value, so the
                // consumer's SCSS fallback — a cap that never bites — applies instead. Writing `NaNpx` here
                // would be worse than useless: it is a valid custom-property value, so it would silently
                // invalidate every `max-height` reading it.
                elements.floating.style.removeProperty(OVERLAY_AVAILABLE_HEIGHT);
                return;
            }

            elements.floating.style.setProperty(
                OVERLAY_AVAILABLE_HEIGHT,
                `${Math.max(MINIMUM_OVERLAY_HEIGHT, available)}px`
            );
        },
    },
};
